<template>
  <PopUp :model-value="visible" size="md" :close-on-overlay="false" @update:model-value="$emit('close')">
    <div class="p-6 relative">
      <LoaderOverlay :active="loading" />
      <h3 class="text-black text-[1.6rem] font-semibold mb-3">{{ resolvedTitle }}</h3>
      <p v-if="prompt" class="text-[1.2rem] text-[#555] mb-4">
        {{ prompt }}
      </p>
      <div class="flex flex-wrap gap-2 mb-4">
        <button
          v-for="option in options"
          :key="option"
          type="button"
          class="px-4 py-2 rounded-[999px] border border-solid border-[rgba(0,0,0,0.12)] text-[1.2rem] font-medium transition-colors flex items-center justify-between gap-3 w-full disabled:opacity-60 disabled:pointer-events-none group hover:bg-black hover:text-white"
          :class="{ 'duration-option--selected': isSelected(option) }"
          :disabled="loading"
          @click="$emit('select', option)"
        >
          <span>{{ option }} {{ $t('min') }}</span>
          <span v-if="loading" :class="priceClass(option)">
            <InlineSpinner size="14" thickness="2" />
          </span>
          <span
            v-else
            :class="priceClass(option)"
          >
            {{ priceLabel(option) || '' }}
          </span>
        </button>
      </div>
      <p v-if="selectedDepositDisplay" class="duration-selector__deposit">
        {{ $t("Deposit today: {amount}", { amount: selectedDepositDisplay }) }}
        · {{ $t("The rest is paid at the center") }}
      </p>
      <button
        type="button"
        class="w-full rounded-[999px] bg-black text-white text-[1.2rem] font-medium py-3 mb-3 disabled:opacity-60 disabled:pointer-events-none"
        :disabled="confirmDisabled"
        @click="$emit('confirm')"
      >
        {{ resolvedConfirmLabel }}
      </button>
      <button
        type="button"
        class="w-full text-[1.2rem] font-medium text-center text-black underline hover:opacity-80 transition-opacity"
        @click="$emit('close')"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </PopUp>
</template>

<script>
import LoaderOverlay from '@/components/loader/loader.vue';
import InlineSpinner from '@/components/loader/InlineSpinner.vue';
import { formatDurationPrompt } from '@/helpers/doctorScheduleSlotHelper';
import { resolveDurationPriceLabel } from '@/helpers/doctorScheduleDurationHelper';
import PopUp from '@/components/popup/PopUp.vue';

export default {
  name: 'DoctorScheduleDurationSelector',
  components: {
    LoaderOverlay,
    InlineSpinner,
    PopUp,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
    },
    date: {
      type: String,
      default: null,
    },
    time: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    prices: {
      type: Object,
      default: () => ({}),
    },
    slotInfo: {
      type: Object,
      default: null,
    },
    selectedDuration: {
      type: [Number, String],
      default: null,
    },
    title: {
      type: String,
      default: 'Select duration',
    },
    confirmLabel: {
      type: String,
      default: 'Confirm and continue',
    },
    depositDisplay: {
      type: String,
      default: null,
    },
  },
  emits: ['select', 'close', 'confirm'],
  computed: {
    selectedDepositDisplay() {
      if (this.selectedDuration !== null && this.selectedDuration !== undefined) {
        const entry = this.prices[Number(this.selectedDuration)];
        if (entry && entry.deposit) {
          return entry.deposit;
        }
      }
      // fall back to the flat listing deposit when per-duration prices are not loaded
      return this.depositDisplay || null;
    },
    resolvedTitle() {
      if (!this.title) {
        return this.$t('Select duration');
      }
      return this.$t(this.title);
    },
    resolvedConfirmLabel() {
      if (!this.confirmLabel) {
        return this.$t('Confirm and continue');
      }
      return this.$t(this.confirmLabel);
    },
    prompt() {
      if (!this.date || !this.time) {
        return '';
      }
      return formatDurationPrompt(this.date, this.time);
    },
    confirmDisabled() {
      if (this.loading) {
        return true;
      }
      return this.selectedDuration === null || this.selectedDuration === undefined;
    },
  },
  methods: {
    priceLabel(option) {
      return resolveDurationPriceLabel(option, this.prices, this.slotInfo);
    },
    isSelected(option) {
      if (this.selectedDuration === null || this.selectedDuration === undefined) {
        return false;
      }
      return String(this.selectedDuration) === String(option);
    },
    priceClass(option) {
      if (this.isSelected(option)) {
        return 'text-white text-[1.1rem]';
      }
      return 'text-[#8B8B8B] text-[1.1rem] group-hover:text-white';
    },
  },
};
</script>

<style scoped>
.duration-option--selected {
  background: #2f2f2f;
  color: #fff;
}

.duration-selector__deposit {
  margin-bottom: 1rem;
  font-size: 1.15rem;
  color: rgba(0, 0, 0, 0.55);
}
</style>
